// "Notify me when it's back" — shown on sold-out and pre-order variants.
import { useEffect, useState } from 'react';
import { BellRing, Users } from 'lucide-react';
import { API_BASE } from '../../lib/apiBase';

const ERRORS = {
        valid_email_required: 'Enter a valid email.',
        too_many_requests: 'Too many sign-ups from this device. Try again in an hour.',
        already_in_stock: 'Good news — this one is in stock right now.',
};

export default function RestockNotify({ handle, variant, kind }) {
        const [email, setEmail] = useState('');
        const [state, setState] = useState('idle');
        const [err, setErr] = useState('');
        const [counts, setCounts] = useState({});
        useEffect(() => { setState('idle'); setErr(''); }, [variant?.id]);
        useEffect(() => {
                let alive = true;
                fetch(`${API_BASE}/api/restock-alerts/count?handle=${encodeURIComponent(handle)}`)
                        .then((r) => r.json())
                        .then((j) => { if (alive && j?.ok) setCounts(j.variants || {}); })
                        .catch(() => {});
                return () => { alive = false; };
        }, [handle]);
        const waiting = counts[variant?.id] || 0;
        if (!variant) return null;

        const submit = async (e) => {
                e.preventDefault();
                setState('busy');
                setErr('');
                const r = await fetch(`${API_BASE}/api/restock-alerts`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ product_handle: handle, variant_id: variant.id, email }),
                }).catch(() => null);
                const j = r ? await r.json().catch(() => ({})) : {};
                if (r?.ok && j.ok) {
                        setState('done');
                        fetch(`${API_BASE}/api/restock-alerts/count?handle=${encodeURIComponent(handle)}`)
                                .then((x) => x.json()).then((x) => x?.ok && setCounts(x.variants || {})).catch(() => {});
                        return;
                }
                setState('idle');
                setErr(ERRORS[j.detail] || 'Could not sign you up. Try again.');
        };

        const lead = kind === 'soldout'
                ? 'Sold out right now. Drop your email and we’ll tell you the moment it’s back.'
                : 'Want it from in-hand stock instead of pre-order? We’ll email you when it lands.';

        return (
                <div data-testid="pdp-restock-notify" className="mt-5 border border-white/15 bg-white/[0.02] px-4 py-4">
                        <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#FF9F1C]">
                                <BellRing className="h-3.5 w-3.5" /> {kind === 'soldout' ? 'Notify me when it’s back' : 'Notify me when in-hand stock lands'}
                        </p>
                        {waiting >= 3 && (
                                <p data-testid="pdp-restock-waiting" className="mt-2 flex items-center gap-1.5 font-mono text-[11px] tracking-[0.12em] uppercase text-white/80">
                                        <Users className="h-3.5 w-3.5 text-[#FF9F1C]" /> {waiting} people waiting
                                </p>
                        )}
                        {state === 'done' ? (
                                <p data-testid="pdp-restock-success" className="mt-2 font-sans text-[13px] text-white/80">
                                        You’re on the list. One email when it’s back — nothing else.
                                </p>
                        ) : (
                                <>
                                        <p className="mt-2 font-sans text-[12px] text-white/60 leading-relaxed">{lead}</p>
                                        <form onSubmit={submit} className="mt-3 flex gap-2">
                                                <input
                                                        data-testid="pdp-restock-email"
                                                        type="email"
                                                        required
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        placeholder="you@email.com"
                                                        maxLength={160}
                                                        className="min-w-0 flex-1 bg-transparent border border-white/20 px-3 py-2.5 font-sans text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF9F1C] transition-colors"
                                                />
                                                <button
                                                        data-testid="pdp-restock-submit"
                                                        type="submit"
                                                        disabled={state === 'busy'}
                                                        className="shrink-0 border border-[#FF9F1C] px-4 font-mono text-[11px] tracking-[0.18em] uppercase text-[#FF9F1C] hover:bg-[#FF9F1C] hover:text-[#0A0A0A] transition-colors disabled:opacity-40"
                                                >
                                                        {state === 'busy' ? '…' : 'Notify me'}
                                                </button>
                                        </form>
                                        {err && <p data-testid="pdp-restock-error" className="mt-2 font-mono text-[11px] text-red-400">{err}</p>}
                                </>
                        )}
                </div>
        );
}
